/**
 * Plan Card Component
 * 
 * Displays a subscription plan with features and pricing.
 */

import React from 'react';
import { Check, Zap } from 'lucide-react';
import { SubscriptionPlan } from '../../types/subscription';

interface PlanCardProps {
  plan: SubscriptionPlan;
  isCurrentPlan?: boolean;
  currentPlanId?: string;
  onSelect: (planId: string) => void;
  loading?: boolean;
  className?: string;
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  isCurrentPlan = false,
  currentPlanId,
  onSelect,
  loading = false,
  className = ''
}) => {
  const handleSelect = () => {
    if (!isCurrentPlan && !loading) {
      // For enterprise plan, open Calendly in new tab
      if (plan.id === 'enterprise') {
        window.open('https://calendly.com/ad-blue-pill/30min', '_blank');
      } else {
        onSelect(plan.id);
      }
    }
  };

  const formatPrice = (price: number, planId: string) => {
    if (price === 0) return 'Free';
    if (planId === 'enterprise') return 'Let\'s Talk';
    return `$${price}`;
  };

  const getButtonText = (targetPlan: SubscriptionPlan, currentPlan?: string) => {
    // Plan hierarchy: free < starter < growth < enterprise
    const planHierarchy = ['free', 'starter', 'growth', 'enterprise'];
    const currentIndex = currentPlan ? planHierarchy.indexOf(currentPlan) : -1;
    const targetIndex = planHierarchy.indexOf(targetPlan.id);
    
    if (currentIndex === -1 || targetIndex > currentIndex) {
      return `Upgrade to ${targetPlan.name}`;
    } else {
      return `Switch to ${targetPlan.name}`;
    }
  };

  const isPaidPlan = plan.price > 0;

  return (
    <div 
      className={`
        relative rounded-lg border-2 p-6 transition-all duration-200 cursor-pointer
        flex flex-col border-gray-200 bg-white hover:border-gray-300 hover:shadow-md
        ${loading ? 'opacity-50 pointer-events-none' : ''}
        ${className}
      `}
      onClick={handleSelect}
    >
      {/* Current Plan Badge */}
      {isCurrentPlan && (
        <div className="absolute -top-3 right-4">
          <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
            <Check className="w-3 h-3 mr-1" />
            Current Plan
          </div>
        </div>
      )}

      {/* Plan Header */}
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {plan.name}
        </h3>
        
        <div className="mb-3">
          <span className="text-4xl font-bold text-gray-900">
            {formatPrice(plan.price, plan.id)}
          </span>
          {isPaidPlan && plan.id !== 'enterprise' && (
            <span className="text-gray-500 ml-1">/month</span>
          )}
        </div>
        
        {plan.description && (
          <p className="text-sm text-gray-600">
            {plan.description}
          </p>
        )}
      </div>

      {/* Features List - flex-grow to push button down */}
      <div className="space-y-3 mb-6 flex-grow">
        <div className="flex items-center">
          <Check className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
          <span className="text-sm font-medium">
            {plan.features.simulationsPerMonth === -1 
              ? 'Unlimited runs per month'
              : `${plan.features.simulationsPerMonth} runs per month`
            }
          </span>
        </div>

        {/* Audience Type Feature */}
        <div className="flex items-center">
          <Check className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
          <span className="text-sm">
            {plan.id === 'free' 
              ? 'Basic surveys access'
              : plan.id === 'starter'
              ? 'Generic target audience'
              : plan.id === 'growth'
              ? 'Custom built AI audience'
              : 'Largest custom data set'
            }
          </span>
        </div>

        {/* Use Cases Feature */}
        <div className="flex items-center">
          <Check className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
          <span className="text-sm">
            {plan.id === 'free'
              ? 'Basic surveys'
              : plan.id === 'starter'
              ? 'Standard use cases (Surveys, Chat, Creative Tests)'
              : plan.id === 'growth'
              ? 'Standard + premium use cases'
              : 'All use cases + tailored workflows'
            }
          </span>
        </div>

        {/* Custom Input Data for Growth+ */}
        {(plan.id === 'growth' || plan.id === 'enterprise') && (
          <div className="flex items-center">
            <Check className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
            <span className="text-sm">
              {plan.id === 'growth' ? 'Custom input data' : 'Largest custom data set'}
            </span>
          </div>
        )}

        {plan.features.prioritySupport && (
          <div className="flex items-center">
            <Zap className="w-4 h-4 text-yellow-500 mr-3 flex-shrink-0" />
            <span className="text-sm font-medium">
              Priority support
            </span>
          </div>
        )}
      </div>

      {/* Action Button - stays at bottom */}
      <button
        onClick={handleSelect}
        disabled={isCurrentPlan || loading}
        className={`
          mt-auto w-full py-3 px-4 rounded-md font-medium transition-colors
          ${isCurrentPlan
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
            : 'bg-gray-900 text-white hover:bg-gray-800'
          }
          ${loading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Processing...
          </div>
        ) : isCurrentPlan ? (
          'Current Plan'
        ) : plan.price === 0 ? (
          'Start Free Trial'
        ) : plan.id === 'enterprise' ? (
          'Contact Sales'
        ) : (
          getButtonText(plan, currentPlanId)
        )}
      </button>

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/50 rounded-lg flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
    </div>
  );
};

export default PlanCard;